import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SxStyle } from '../../../../types';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogContent from '@mui/material/DialogContent';
import { useStringsContext } from '../../../../contexts/strings';
import DialogActions from '@mui/material/DialogActions';
import { useStaff } from '../hooks/staff';
import { StaffMember, StaffRole } from '../api-types';
import { FormControl, Grid } from '@mui/material';
import { getStaffMemberUiSchema } from '../UI-schema';
import RenderModuleField from '../../../common/fields/render-fields';
import BaseDialog, { BaseDialogProps } from '../../../common/base-dialog';

interface Props extends Pick<BaseDialogProps, 'open' | 'onClose'> {
  onDone: () => void;
  teamId: string;
  season: string;
  member?: StaffMember;
}

const styles: SxStyle = {
  contentList: {
    padding: '16px',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100px',
  },
};

export default function StaffMemberModal({ open, onClose, onDone, member, teamId, season }: Props) {
  const { strings } = useStringsContext();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const { addStaffMembersToTeam, editStaffMembers } = useStaff();
  const valuesFields = useRef<Record<string, any>>({ role: '' });
  const editedFields = useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (open) {
      valuesFields.current = {};
      editedFields.current = {};
    }
  }, [open, member]);

  const onSubmit = useCallback(async () => {
    setSubmitLoading(true);
    try {
      const updatedMember: StaffMember = valuesFields.current as StaffMember;

      if (member) {
        await editStaffMembers(teamId, season, [{ ...member, ...updatedMember }]);
      } else {
        await addStaffMembersToTeam(teamId, season, [updatedMember]);
      }

      onDone();
      onClose?.();
    } finally {
      setSubmitLoading(false);
    }
  }, [onClose, onDone, season, teamId, addStaffMembersToTeam, editStaffMembers, member]);

  const staffMemberUiScheme = useMemo(
    () => getStaffMemberUiSchema(member || { role: StaffRole.COACH }),
    [member]
  );

  const onFieldChange = (key: string, value: any) => {
    valuesFields.current[key] = value;
  };

  const onValidation = async (key: string, value: any, validation: any) => {
    const isCurrentFieldValid: boolean = await validation.isValid(value);
    editedFields.current[key] = isCurrentFieldValid;
    const isFormValid = Object.values(editedFields.current).every(Boolean);
    setSubmitDisabled(!isFormValid);
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={member ? strings.staff.editMember : strings.staff.addMember}
    >
      <DialogContent sx={styles.contentList} dividers>
        <FormControl fullWidth sx={styles.content}>
          <Grid item container spacing={3} direction="column">
            {staffMemberUiScheme.fields.map((field) => (
              <Grid item xs key={field.id}>
                <RenderModuleField
                  field={field}
                  setChange={onFieldChange}
                  setValidation={onValidation}
                  path={teamId}
                  fullWidth
                />
              </Grid>
            ))}
          </Grid>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {strings.actions.cancel}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          disabled={submitDisabled}
          loading={submitLoading}
          onClick={onSubmit}
        >
          {strings.actions.save}
        </LoadingButton>
      </DialogActions>
    </BaseDialog>
  );
}
